import { z } from "zod";

//#region ../../node_modules/@jwplayer/fe-common/types-0ZBLQvRi.js
/**
* Type guard for undefined types
*/
function isDefined(v) {
	return !(typeof v === "undefined");
}

//#endregion
//#region ../../node_modules/@jwplayer/fe-common/module/cookies/index.js
function formatCookie(key, value, config) {
	if (key.trim() === "") throw new Error("Key may not be empty");
	return [`${key}=${value}`, ...Object.entries(config ?? {}).map(([configKey, configValue]) => {
		if (typeof configValue === "undefined") return;
		if (configValue === true) return configKey;
		if (configValue instanceof Date) configValue = configValue.toUTCString();
		return `${configKey}=${configValue}`;
	})].filter(isDefined).join("; ");
}
const documentCookie = (() => {
	let mockCookie = "";
	return {
		set(value) {
			if (typeof document === "undefined" || typeof document.cookie === "undefined") mockCookie = value;
			else document.cookie = value;
		},
		get() {
			if (typeof document === "undefined" || typeof document.cookie === "undefined") return mockCookie;
			return document.cookie;
		}
	};
})();
/**
* Set a cookie
*/
function setCookie(key, value, config) {
	const cookieString = formatCookie(key, value, config);
	documentCookie.set(cookieString);
}
/**
* Delete a cookie
*/
function deleteCookie(key, config) {
	documentCookie.set(formatCookie(key, "", {
		...config,
		"Max-Age": -999999
	}));
}
/**
* Get a cookie's value
*/
function getCookie(key) {
	const cookies = documentCookie.get().split(";").map((i) => i.trim());
	for (const cookie of cookies) {
		const parts = cookie.split("=");
		if (parts.length < 2) continue;
		const name = parts.shift();
		if (name === key) return parts.join("=");
	}
	return void 0;
}
function createCookieManager(config) {
	let _config = config;
	return {
		setConfig(config$1) {
			_config = config$1;
		},
		getCookie,
		setCookie: (key, value, config$1) => {
			setCookie(key, value, {
				...config$1,
				..._config
			});
		},
		deleteCookie: (key, config$1) => {
			deleteCookie(key, {
				...config$1,
				..._config
			});
		},
		getConfig() {
			return { ..._config };
		}
	};
}

//#endregion
//#region ../base/util/environment.ts
function getEnvVariable(variable) {
	if (typeof window !== "undefined") {
		let envVar;
		try {
			envVar = import.meta.env[variable];
		} catch {}
		return envVar;
	}
	if (typeof process !== "undefined" && "versions" in process && typeof process.versions === "object" && "node" in process.versions && typeof process.versions.node === "string") return process.env[variable];
	throw new Error("Unsupported environment");
}
function getDomain() {
	if (typeof window === "undefined") return void 0;
	return window.location.hostname.includes("jwplayer.com") ? "jwplayer.com" : void 0;
}

//#endregion
//#region ../base/auth/cookies.ts
const cookieManager = createCookieManager({
	Path: "/",
	Domain: getDomain(),
	Secure: getEnvVariable("TEST") ? void 0 : true,
	SameSite: "strict"
});

//#endregion
//#region ../base/auth/from-admin.ts
const zAuthFromAdminMetadata = z.object({
	readOnly: z.boolean(),
	username: z.string(),
	date: z.string()
});
/**
* Verify that the auth from admin payload has the correct properties
*/
function isAuthFromAdminMetadata(data) {
	return Boolean(data && typeof data === "object" && "readOnly" in data && typeof data.readOnly === "boolean" && "username" in data && typeof data.username === "string" && data.username && "date" in data && typeof data.date === "string" && data.date);
}
function isAuthFromAdminPayload(data) {
	return isAuthFromAdminMetadata(data) && "authToken" in data && typeof data.authToken === "string";
}
function decodePayload(str) {
	const payload = JSON.parse(atob(str));
	if (!isAuthFromAdminPayload(payload)) throw new Error("Invalid payload");
	return payload;
}
function getAuthFromAdminPayloadFromUrl(queryParam) {
	if (typeof window === "undefined") return;
	const usp = new URLSearchParams(window.location.search);
	const value = usp.get(queryParam);
	if (!value) return;
	try {
		return decodePayload(value);
	} catch {
		console.warn("Login through admin failed. Invalid payload.");
	}
}
/**
* Check if current session was established in the admin dashboard and get metadata.
*/
function getAuthFromAdminMetadataFromCookie(cookieName) {
	const value = cookieManager.getCookie(cookieName);
	if (!value) return;
	try {
		const meta = JSON.parse(value);
		if (isAuthFromAdminMetadata(meta)) return meta;
	} catch {}
}

//#endregion
export { cookieManager, getAuthFromAdminMetadataFromCookie, getAuthFromAdminPayloadFromUrl, getEnvVariable, zAuthFromAdminMetadata };